"use client"

import { Loader2 } from "lucide-react"
import { usePermissions } from "@/hooks/use-permissions"
import { useInitialSyncPart, type InitialSyncPart } from "@/hooks/queries/shopify-initial-sync"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"



const NOUN: Record<InitialSyncPart, string> = {
  orders: "orders",
  catalog: "products",
}

const DURATION_HINT: Record<InitialSyncPart, string> = {
  orders: "This usually takes a few minutes. The page updates on its own.",
  // A catalog bulk export is handed to Shopify and collected afterwards — it is
  // routinely the slower of the two.
  catalog: "A full catalog export can take several minutes. The page updates on its own.",
}

/**
 * Replaces the "nothing here" copy on data pages while the first load is still running,
 * so an empty table is not read as a broken integration. Renders nothing otherwise.
 */
export function InitialSyncHint({
  part,
  integrationId,
}: {
  part: InitialSyncPart
  integrationId?: string | null
}) {
  const tUi = useTranslations("Shopify.ShopifyInitialSyncHint")

  const { can } = usePermissions()
  const state = useInitialSyncPart(part, integrationId)

  if (!state) return null

  if (state.status === "error") {
    return (
      <span role="status" aria-live="polite" className="flex flex-col items-center gap-1">
        <span>{state.message}</span>
        {can(["integrations.*"]) ? (
          <a href="?page=integrations" className="underline underline-offset-2">
            <span key={tUi("goToIntegrations")}>{tUi("goToIntegrations")}</span></a>
        ) : (
          <span><span key={tUi("contactAnAdministratorToReconnectThisStore")}>{tUi("contactAnAdministratorToReconnectThisStore")}</span></span>
        )}
      </span>
    )
  }

  return (
    <span role="status" aria-live="polite" className="flex flex-col items-center gap-1">
      <span className="flex items-center gap-2"><TranslationText>{tUi.rich("tag0Tag0LoadingYourValue0FromShopify", { value0: String(NOUN[part]), tag0: () => (<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />) })}</TranslationText></span>
      <span className="text-xs">{DURATION_HINT[part]}</span>
    </span>
  )
}
